import { useMemo, useState } from "react";
import { CheckIcon, PlusCircleIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@capacit-com/lara-law-shared-ui";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { FilterOption } from "@/models/base/Filter";
import {
  FilterOptionLabel,
  MinistryFilterHelp,
} from "@/components/data-table/filters/filter-option-alias-display";
import {
  formatSelectedOptionTooltipHtml,
  MINISTRY_FILTER_POPOVER_CLASS,
} from "@/components/data-table/filters/filter-option-alias-utils";
import {
  filterOptionMatchesQuery,
  MINISTRY_FILTER_KEY,
} from "@/pages/legislation/utils/ministryFilterOptions";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

interface StandaloneFacetedFilterProps {
  title: string;
  options: FilterOption[];
  selectedValues: string[];
  onValueChange: (values: string[]) => void;
  placeholder?: string;
  selectionMode?: "single" | "multi";
  /** Filter config key (e.g. ministerium) for contextual help. */
  filterKey?: string;
  /** Hide the selected values shown below the button (default: false) */
  hideSelectedLabels?: boolean;
}

export function StandaloneFacetedFilter({
  title,
  options,
  selectedValues,
  onValueChange,
  placeholder,
  selectionMode = "multi",
  filterKey,
  hideSelectedLabels = false,
}: StandaloneFacetedFilterProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const selectedSet = new Set(selectedValues);
  const showMinistryHelp = filterKey === MINISTRY_FILTER_KEY;

  const visibleOptions = useMemo(() => {
    if (!searchQuery.trim()) return options;
    return options.filter(
      (option) => filterOptionMatchesQuery(option, searchQuery).matches
    );
  }, [options, searchQuery]);

  const selectedOptions = selectedValues
    .map((v) => options.find((o) => o.value === v))
    .filter((o): o is FilterOption => o != null);

  return (
    <div className="flex flex-col">
      <Popover
        onOpenChange={(open) => {
          if (!open) setSearchQuery("");
        }}
      >
        <div className="flex items-center gap-1">
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={cn(
                "h-8 min-w-0 flex-1 border-dashed text-xs justify-start gap-1",
                selectedValues.length > 0 && "border-solid"
              )}
            >
              <PlusCircleIcon className="size-3.5 shrink-0" />
              <span>{title}</span>
              <span className="flex-1" />
              {selectedValues.length > 0 && (
                <Badge
                  variant="secondary"
                  className="rounded-sm px-1.5 font-normal"
                >
                  {selectedValues.length}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          {/* Beside the trigger, not inside it: a <button> cannot contain a <button>. */}
          {showMinistryHelp && <MinistryFilterHelp className="shrink-0" />}
        </div>
        <PopoverContent
          className={cn(
            showMinistryHelp ? MINISTRY_FILTER_POPOVER_CLASS : "p-0",
            "z-130"
          )}
          align="start"
          onClick={(e) => e.stopPropagation()}
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={placeholder || title}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>Ingen resultater.</CommandEmpty>
              <CommandGroup>
                {visibleOptions.map((option) => {
                  const isSelected = selectedSet.has(option.value);
                  const { matchedViaAlias } = filterOptionMatchesQuery(
                    option,
                    searchQuery
                  );
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      className="items-start gap-2 py-2"
                      onSelect={() => {
                        const newValues = (() => {
                          if (selectionMode === "single") {
                            return isSelected ? [] : [option.value];
                          }
                          return isSelected
                            ? selectedValues.filter((v) => v !== option.value)
                            : [...selectedValues, option.value];
                        })();
                        onValueChange(newValues);
                      }}
                    >
                      <div
                        className={cn(
                          "mt-0.5 mr-2 flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <CheckIcon className="size-4 text-primary-foreground" />
                      </div>
                      {option.icon && (
                        <option.icon className="mr-2 size-4 shrink-0 text-muted-foreground" />
                      )}
                      <FilterOptionLabel
                        option={option}
                        matchedViaAlias={matchedViaAlias}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
              {selectedValues.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => onValueChange([])}
                      className="justify-center text-center"
                    >
                      Ryd filter
                    </CommandItem>
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {!hideSelectedLabels && selectedOptions.length > 0 && (
        <div className="mt-1 flex flex-col gap-0.5">
          {selectedOptions.map((option) => (
            <Tooltip key={option.value}>
              <TooltipTrigger asChild>
                <div
                  className="whitespace-normal break-words text-xs font-semibold text-foreground leading-snug"
                  title={option.label}
                >
                  {option.label}
                </div>
              </TooltipTrigger>
              {option.aliases && option.aliases.length > 0 && (
                <TooltipContent side="bottom" className="max-w-xs">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: formatSelectedOptionTooltipHtml(option),
                    }}
                  />
                </TooltipContent>
              )}
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
}
